import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ImagePlus, Loader2, Plus, Save, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const PRESET = ["Make / Model", "Type / Configuration", "Working Pressure (bar g)", "Free Air Delivery (CFM / m³/min)",
  "Motor Rating (kW / HP)", "Power Supply (V / Ph / Hz)", "Starter Type", "Net Weight (kg)", "Overall Dimensions (L × W × H, mm)"];

const SpecRows = ({ specs, setSpecs }) => {
  const upd = (i, patch) => setSpecs(specs.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  return (
    <div className="space-y-1.5">
      {specs.map((s, i) => (
        <div key={i} className="flex flex-col gap-1.5 border-b border-border pb-1.5 sm:flex-row sm:border-0 sm:pb-0" data-testid={`spec-row-${i}`}>
          <Input className="h-8 sm:w-2/5" placeholder="Parameter" value={s.parameter} onChange={(e) => upd(i, { parameter: e.target.value })} data-testid={`spec-param-${i}`} />
          <Input className="h-8 flex-1" placeholder="Specification" value={s.value} onChange={(e) => upd(i, { value: e.target.value })} data-testid={`spec-value-${i}`} />
          <Button type="button" variant="ghost" size="sm" onClick={() => setSpecs(specs.filter((_, j) => j !== i))} data-testid={`spec-remove-${i}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
        </div>
      ))}
    </div>
  );
};

const PhotoBox = ({ image, setImage }) => {
  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Please use a photo under 2 MB"); return; }
    const r = new FileReader();
    r.onload = () => setImage(r.result);
    r.readAsDataURL(f);
  };
  return (
    <div className="flex items-center gap-3">
      <div className="relative flex h-28 w-36 items-center justify-center rounded-md border border-dashed border-border bg-slate-50" data-testid="spec-photo-preview">
        {image ? <img src={image} alt="Product" className="max-h-24 max-w-[8.5rem] object-contain" /> : <span className="text-[11px] text-muted-foreground">No Photo</span>}
        {image && <button type="button" onClick={() => setImage("")} className="absolute right-1 top-1 rounded bg-white/90 p-0.5" data-testid="spec-photo-remove"><X className="h-3.5 w-3.5 text-rose-600" /></button>}
      </div>
      <label className="inline-flex cursor-pointer items-center rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-slate-50" data-testid="spec-photo-upload">
        <ImagePlus className="mr-1.5 h-4 w-4" />{image ? "Replace Photo" : "Upload Photo"}
        <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pick} data-testid="spec-photo-input" />
      </label>
    </div>
  );
};

export const TechSpecEditor = ({ productId }) => {
  const [specs, setSpecs] = useState([]);
  const [image, setImage] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!productId) return;
    api.get(`/catalog/products/${productId}/spec`).then((r) => { setSpecs(r.data.specs || []); setImage(r.data.image_b64 || ""); }).catch(() => {});
  }, [productId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/catalog/products/${productId}/spec`, { specs, image_b64: image });
      toast.success("Technical specification saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!productId) return <p className="text-xs text-muted-foreground">Save the product first to add its photo and technical specification.</p>;
  return (
    <div className="space-y-3 rounded-md border border-border p-3" data-testid="tech-spec-editor">
      <p className="text-[11px] text-muted-foreground">Prints as "Equipment Technical Specification" on any client quote that uses this SKU.</p>
      <div className="space-y-1.5"><Label className="text-xs text-slate-600">Equipment Photograph</Label><PhotoBox image={image} setImage={setImage} /></div>
      <div className="space-y-1.5"><Label className="text-xs text-slate-600">Parameters</Label><SpecRows specs={specs} setSpecs={setSpecs} /></div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => setSpecs([...specs, { parameter: "", value: "" }])} data-testid="spec-add-row"><Plus className="mr-1 h-3.5 w-3.5" />Add Row</Button>
        {!specs.length && <Button type="button" variant="outline" size="sm" onClick={() => setSpecs(PRESET.map((p) => ({ parameter: p, value: "" })))} data-testid="spec-use-preset">Use Equipment Template</Button>}
        <Button type="button" size="sm" className="ml-auto bg-[#1B4383] hover:bg-[#16386e]" onClick={save} disabled={saving} data-testid="spec-save">
          {saving ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />}Save Specification
        </Button>
      </div>
    </div>
  );
};

export default TechSpecEditor;
